import type { LabeledTextGridComponent } from "../../types";

const TITLE_COLOR = "#00B862";
const COLS: Record<number, string> = {
  2: "grid-cols-2",
  3: "grid-cols-3",
  4: "grid-cols-4",
};

function toPerRow(v: unknown): 2 | 3 | 4 {
  const n = Number(v);
  return n >= 2 && n <= 4 ? (n as 2 | 3 | 4) : 2;
}

function Card({ title, lines }: { title: string; lines: string[] }) {
  return (
    <div className="flex h-full flex-col text-left">
      <h3 className="text-base xl:text-lg font-semibold md:text-[17px]" style={{ color: TITLE_COLOR }}>
        {title}
      </h3>
      <div className="mt-2 space-y-1 text-sm xl:text-base md:text-base text-black">
        {lines.map((line, i) => (
          <p key={i}>{line}</p>
        ))}
      </div>
    </div>
  );
}

/** Labeled text grid renderer with optional last-row centering. */
export function LabeledTextGridRenderer({ component }: { component: LabeledTextGridComponent }) {
  const { items, cardsPerRowTablet, cardsPerRowDesktop, centerLastRow: centerLastRowOpt } = component.content;
  const centerLastRow = centerLastRowOpt === true || centerLastRowOpt === "true";
  const tablet = toPerRow(cardsPerRowTablet);
  const desktop = toPerRow(cardsPerRowDesktop);
  const gridCols = `sm:grid-cols-2 md:${COLS[tablet]} lg:${COLS[desktop]}`;

  if (!centerLastRow) {
    return (
      <section className="w-full">
        <div className={`grid w-full grid-flow-dense gap-6 md:gap-8 ${gridCols} justify-items-start`}>
          {items.map((item, i) => (
            <Card key={`${item.title}-${i}`} title={item.title} lines={item.lines ?? []} />
          ))}
        </div>
      </section>
    );
  }

  const fullCount = Math.floor(items.length / desktop) * desktop;
  const fullRows = items.slice(0, fullCount);
  const lastRow = items.slice(fullCount);

  return (
    <section>
      {fullRows.length > 0 && (
        <div className={`grid gap-6 md:gap-8 ${gridCols}`}>
          {fullRows.map((item, i) => (
            <Card key={`${item.title}-${i}`} title={item.title} lines={item.lines ?? []} />
          ))}
        </div>
      )}
      {lastRow.length > 0 && (
        <div
          className="labeled-text-grid-partial-row mt-6 grid gap-6 md:mt-8 md:gap-8"
          style={{
            gridTemplateColumns: `repeat(${lastRow.length}, 1fr)`,
            maxWidth: `${(lastRow.length / desktop) * 100}%`,
            marginLeft: "auto",
            marginRight: "auto",
          }}
        >
          {lastRow.map((item, i) => (
            <Card key={`${item.title}-${fullCount + i}`} title={item.title} lines={item.lines ?? []} />
          ))}
        </div>
      )}
    </section>
  );
}
